<template>
  <div v-if="isOpen" class="associations-floating" :style="floatingStyle">
    <div class="associations-header" @mousedown="startDrag">
      <div class="d-flex align-center">
        <v-icon small class="mr-2" color="white">mdi-link-variant</v-icon>
        <span class="font-weight-medium white--text">Asociaciones</span>
      </div>
      <v-btn icon x-small @click="close" color="white">
        <v-icon small>mdi-close</v-icon>
      </v-btn>
    </div>
    <div class="associations-body">
      <div v-if="loading" class="d-flex justify-center align-center pa-6">
        <v-progress-circular indeterminate color="primary" size="28"></v-progress-circular>
      </div>
      <div v-else-if="error" class="pa-4 text-caption error--text">
        No se pudieron cargar las asociaciones.
      </div>
      <div v-else-if="associations.length === 0" class="pa-4 text-caption grey--text text--darken-1">
        Este archivo no tiene asociaciones.
      </div>
      <v-list v-else dense class="py-0">
        <template v-for="(assoc, index) in associations">
          <v-list-item :key="assoc.id">
            <v-list-item-icon class="mr-3">
              <v-icon :color="getTypeInfo(assoc.relatedType).color" small>
                {{ getTypeInfo(assoc.relatedType).icon }}
              </v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="text-body-2 font-weight-medium">
                {{ assoc.related.title || assoc.related.name || 'Sin título' }}
              </v-list-item-title>
              <v-list-item-subtitle class="text-caption">
                <v-chip x-small label outlined color="grey darken-1" class="mr-2">
                  {{ getTypeInfo(assoc.relatedType).label }}
                </v-chip>
                <span v-if="assoc.related.number">N° {{ assoc.related.number }}</span>
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-divider v-if="index < associations.length - 1" :key="'div-' + assoc.id"></v-divider>
        </template>
      </v-list>
    </div>
  </div>
</template>

<script>
import { getFileAssociations } from '@/helpers/api/fileManager'

export default {
  name: 'AssociationsListModal',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    fileId: {
      type: [Number, String],
      default: null
    }
  },
  data() {
    return {
      isOpen: false,
      loading: false,
      error: false,
      associations: [],
      position: { x: null, y: null },
      dragging: false,
      dragOffset: { x: 0, y: 0 },
      typeMap: {
        'App\\Models\\Rdi': { label: 'Rdi' },
        'App\\Models\\TasksRestrictions': { label: 'Tareas' },
        'App\\Models\\Entrega': { label: 'Entrega' },
        'App\\Models\\Additional': { label: 'Adicionales' },
        'App\\Models\\FilesCenter': { label: 'Archivo' }
      }
    }
  },
  computed: {
    floatingStyle() {
      if (this.position.x === null || this.position.y === null) {
        return {}
      }
      return {
        left: `${this.position.x}px`,
        top: `${this.position.y}px`,
        right: 'auto',
        bottom: 'auto'
      }
    }
  },
  watch: {
    value(newVal) {
      this.isOpen = newVal
      if (newVal) {
        this.fetchAssociations()
      }
    }
  },
  mounted() {
    this.isOpen = this.value
    if (this.isOpen) {
      this.fetchAssociations()
    }
  },
  beforeDestroy() {
    document.removeEventListener('mousemove', this.onDrag)
    document.removeEventListener('mouseup', this.stopDrag)
  },
  methods: {
    getTypeInfo(type) {
      const info = this.typeMap[type] || { label: type ? type.split('\\').pop() : 'Asociación' }
      return { ...info, icon: 'mdi-shape-outline', color: 'grey darken-1' }
    },
    async fetchAssociations() {
      if (!this.fileId) return
      this.loading = true
      this.error = false
      try {
        const response = await getFileAssociations({ file_id: this.fileId })
        const rawAssociations = response?.data?.item_associations || []
        this.associations = rawAssociations.map((assoc) => {
          const isFirstTheFile = assoc.first_morphable_type === 'App\\Models\\FilesCenter'
          const related = isFirstTheFile ? assoc.second_morphable : assoc.first_morphable
          const relatedType = isFirstTheFile ? assoc.second_morphable_type : assoc.first_morphable_type
          return {
            id: assoc.id,
            related: related || {},
            relatedType
          }
        })
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },
    close() {
      this.isOpen = false
      this.$emit('input', false)
    },
    startDrag(event) {
      this.dragging = true
      const rect = event.currentTarget.parentElement.getBoundingClientRect()
      if (this.position.x === null) {
        this.position.x = rect.left
        this.position.y = rect.top
      }
      this.dragOffset.x = event.clientX - this.position.x
      this.dragOffset.y = event.clientY - this.position.y
      document.addEventListener('mousemove', this.onDrag)
      document.addEventListener('mouseup', this.stopDrag)
    },
    onDrag(event) {
      if (!this.dragging) return
      let newX = event.clientX - this.dragOffset.x
      let newY = event.clientY - this.dragOffset.y
      const maxX = window.innerWidth - 320
      const maxY = window.innerHeight - 40
      newX = Math.min(Math.max(newX, 0), maxX)
      newY = Math.min(Math.max(newY, 0), maxY)
      this.position.x = newX
      this.position.y = newY
    },
    stopDrag() {
      this.dragging = false
      document.removeEventListener('mousemove', this.onDrag)
      document.removeEventListener('mouseup', this.stopDrag)
    }
  }
}
</script>

<style scoped>
.associations-floating {
  position: fixed;
  top: 90px;
  right: 24px;
  width: 320px;
  max-height: 60vh;
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
  z-index: 300;
  overflow: hidden;
}

.associations-header {
  background: #2D4B95;
  color: white;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: move;
  user-select: none;
}

.associations-body {
  overflow-y: auto;
  flex: 1;
}
</style>